import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import Assets from "@/pages/Assets";
import AssetDetail from "@/pages/AssetDetail";
import AssetForm from "@/pages/AssetForm";
import Users from "@/pages/Users";
import RolesPermissions from "@/pages/RolesPermissions";
import RolesPermissionsEdit from "@/pages/RolesPermissionsEdit";
import CompanySettings from "@/pages/CompanySettings";
import AssetNameOptions from "@/pages/AssetNameOptions";
import PublicAssetStatus from "@/pages/PublicAssetStatus";
import Maintenance from "@/pages/Maintenance";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import EmailTemplates from "@/pages/EmailTemplates";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Vendors from "@/pages/Vendors";
import PurchaseOrderForm from "@/pages/PurchaseOrderForm";
import PurchaseOrderDetail from "@/pages/PurchaseOrderDetail";
import Consumables from "@/pages/Consumables";
import ConsumableLedger from "@/pages/ConsumableLedger";
import ConsumableRequests from "@/pages/ConsumableRequests";
import ConsumableRequestNew from "@/pages/ConsumableRequestNew";
import ConsumableRequestDetail from "@/pages/ConsumableRequestDetail";
import MaterialTransfers from "@/pages/MaterialTransfers";
import MaterialTransferNew from "@/pages/MaterialTransferNew";
import MaterialTransferDetail from "@/pages/MaterialTransferDetail";
import AuditPage, { AuditDetailPage } from "@/pages/Audit";
import TechnicianView from "@/pages/TechnicianView";
import Synopsis from "@/pages/Synopsis";
import ForceChangePassword from "@/pages/ForceChangePassword";
import BreakdownReport from "@/pages/BreakdownReport";
import MaintenanceKanban from "@/pages/MaintenanceKanban";
import ExternalRepairs from "@/pages/ExternalRepairs";
import SLASettings from "@/pages/SLASettings";
import PeopleList from "@/pages/PeopleList";
import EmployeeForm from "@/pages/EmployeeForm";
import EmployeeDetail from "@/pages/EmployeeDetail";
import OrgMasters from "@/pages/OrgMasters";
import ApprovalQueue from "@/pages/ApprovalQueue";
import MyProfile from "@/pages/MyProfile";
import OrgDirectory from "@/pages/OrgDirectory";
import ChecklistTemplates from "@/pages/ChecklistTemplates";
import OnboardingInvite from "@/pages/OnboardingInvite";
import PeopleBulkUpload from "@/pages/PeopleBulkUpload";

function ProtectedRoute({ children, allowForceChange = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 font-heading">Loading Asset IQ...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force password reset on first login for bulk-created / admin-reset users.
  // The change-password page itself must be allowed through, else infinite redirect.
  if (user.force_password_reset && !allowForceChange) {
    return <Navigate to="/change-password-required" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/public/asset/:assetId" element={<PublicAssetStatus />} />
          <Route path="/people/invite/:token" element={<OnboardingInvite />} />
          <Route
            path="/change-password-required"
            element={
              <ProtectedRoute allowForceChange>
                <ForceChangePassword />
              </ProtectedRoute>
            }
          />
          <Route path="/technician" element={<ProtectedRoute><TechnicianView /></ProtectedRoute>} />
          <Route path="/synopsis" element={<ProtectedRoute><Synopsis /></ProtectedRoute>} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="assets" element={<Assets />} />
            <Route path="assets/new" element={<AssetForm />} />
            <Route path="assets/:id" element={<AssetDetail />} />
            <Route path="assets/:id/edit" element={<AssetForm />} />
            <Route path="purchase-orders" element={<PurchaseOrders />} />
            <Route path="purchase-orders/new" element={<PurchaseOrderForm />} />
            <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
            <Route path="purchase-orders/:id/edit" element={<PurchaseOrderForm />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="consumables" element={<Consumables />} />
            <Route path="consumables/ledger" element={<ConsumableLedger />} />
            <Route path="consumable-requests" element={<ConsumableRequests />} />
            <Route path="consumable-requests/new" element={<ConsumableRequestNew />} />
            <Route path="consumable-requests/:id" element={<ConsumableRequestDetail />} />
            <Route path="material-transfers" element={<MaterialTransfers />} />
            <Route path="material-transfers/new" element={<MaterialTransferNew />} />
            <Route path="material-transfers/:id" element={<MaterialTransferDetail />} />
            <Route path="audits" element={<AuditPage />} />
            <Route path="audits/:id" element={<AuditDetailPage />} />
            <Route path="maintenance" element={<Maintenance />} />
            <Route path="maintenance/kanban" element={<MaintenanceKanban />} />
            <Route path="maintenance/external-repairs" element={<ExternalRepairs />} />
            <Route path="breakdown-report" element={<BreakdownReport />} />
            <Route path="settings/sla" element={<SLASettings />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="email-templates" element={<EmailTemplates />} />
            <Route path="reports" element={<Reports />} />
            <Route path="users" element={<Users />} />
            <Route path="roles-permissions" element={<RolesPermissions />} />
            <Route path="roles-permissions/edit" element={<RolesPermissionsEdit />} />
            <Route path="company-settings" element={<CompanySettings />} />
            <Route path="asset-name-options" element={<AssetNameOptions />} />
            <Route path="people" element={<PeopleList />} />
            <Route path="people/directory" element={<OrgDirectory />} />
            <Route path="people/approvals" element={<ApprovalQueue />} />
            <Route path="people/me" element={<MyProfile />} />
            <Route path="people/templates" element={<ChecklistTemplates />} />
            <Route path="people/new" element={<EmployeeForm />} />
            <Route path="people/bulk-upload" element={<PeopleBulkUpload />} />
            <Route path="people/org" element={<OrgMasters />} />
            <Route path="people/:id" element={<EmployeeDetail />} />
            <Route path="people/:id/edit" element={<EmployeeForm />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
